import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Avatar,
} from '@mui/material';
import axios from 'axios';

const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    // Vérifier au chargement si un token existe pour rester connecté
    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token && location.pathname.startsWith('/admin')) {
            setIsAdmin(true);
        } else if (!location.pathname.startsWith('/admin')) {
            setIsAdmin(false);
        }
    }, [location.pathname]);

    const handleOpen = async () => {
        // Vérifier si un token existe déjà
        const token = localStorage.getItem('adminToken');
        if (token) {
            // Vérifier si le token est toujours valide
            try {
                await axios.get('/api/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                // Token valide, rediriger directement
                setIsAdmin(true);
                navigate('/admin');
                return;
            } catch (error) {
                // Token invalide ou expiré, supprimer et demander connexion
                localStorage.removeItem('adminToken');
            }
        }
        // Pas de token ou token invalide, ouvrir le dialogue de connexion
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEmail('');
        setPassword('');
        setError('');
    };

    const handleSubmit = async () => {
        try {
            const response = await axios.post('/api/auth/login', {
                email,
                password
            });

            if (response.data.token) {
                // Stocker le token dans le localStorage
                localStorage.setItem('adminToken', response.data.token);
                setIsAdmin(true);
                navigate('/admin');
                handleClose();
            }
        } catch (error) {
            setError('Email ou mot de passe incorrect');
        }
    };

    const handleHomeClick = () => {
        navigate('/');
    };

    const handleLogout = () => {
        setIsAdmin(false);
        localStorage.removeItem('adminToken'); // Supprimer le token lors de la déconnexion
        navigate('/');
    };

    return (
        <AppBar position="static" sx={{ bgcolor: '#FF721D', minHeight: '80px', maxWidth: '1200px', margin: '0 auto' }}>
            <Toolbar sx={{
                minHeight: '80px',
                paddingY: '8px',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'center' },
                justifyContent: { xs: 'center', sm: 'center' },
                gap: { xs: '8px', sm: '0' }
            }}>
                <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', sm: 'left' }, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Avatar
                        src="/uploads/products/logolp.jpg"
                        alt="Lagune Plongée Logo"
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                transition: 'transform 0.2s ease-in-out'
                            }
                        }}
                        onClick={handleHomeClick}
                    >
                        LP
                    </Avatar>
                    <Typography
                        variant="h6"
                        onClick={handleHomeClick}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            lineHeight: 1.2
                        }}
                    >
                        Lagune plongée
                    </Typography>
                </Box>
                <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            lineHeight: 1.2
                        }}
                    >
                        Bienvenue sur notre site de réservation
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row' }, gap: '8px' }}>
                    <Button color="inherit" onClick={handleHomeClick}>
                        Accueil
                    </Button>
                    <Button color="inherit" onClick={() => navigate('/contact')}>
                        Contact
                    </Button>
                    {isAdmin && (
                        <>
                            <Button color="inherit" onClick={() => navigate('/admin/open-slots')}>
                                Créneaux ouverts
                            </Button>
                            <Button 
                                color="inherit" 
                                onClick={handleLogout}
                                sx={{ 
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.3)'
                                    }
                                }}
                            >
                                🚪 Déconnexion
                            </Button>
                        </>
                    )}
                    {!isAdmin && (
                        <Button color="inherit" onClick={handleOpen}>
                            Accès Admin
                        </Button>
                    )}
                </Box>
            </Toolbar>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Accès Administrateur</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Email"
                        type="email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!error}
                    />
                    <TextField
                        margin="dense"
                        label="Mot de passe"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!error}
                        helperText={error}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Annuler</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Se connecter
                    </Button>
                </DialogActions>
            </Dialog>
        </AppBar>
    );
};

export default Header;